/**
 * @file Mobile Nav.
 */

import debounce from 'just-debounce-it';
import { gsap } from 'gsap';
import * as focusTrap from 'focus-trap';

/**
 * Mobile Nav component.
 */
const vMobileNav = () => {
	const mobileNav = document.querySelector('.v-mobile-nav');

	if (!mobileNav) {
		return;
	}

	const overlay = mobileNav.querySelector('.v-mobile-nav__overlay');
	const modal = mobileNav.querySelector('.v-mobile-nav__modal');
	const wrap = mobileNav.querySelector('.v-mobile-nav__wrap');
	const close = mobileNav.querySelector('.v-mobile-nav__close');
	const stickyHeader = document.querySelector('.v-sticky-header');
	const header = document.querySelector('.v-header');

	// Keyboard trap.
	const trap = focusTrap.createFocusTrap(mobileNav, {
		clickOutsideDeactivates: false,
		onDeactivate:            () => {
			const event = new CustomEvent('v-mobile-nav-toggle', { detail: { open: false } });
			document.dispatchEvent(event);
		},
	});

	// Set initial state.
	gsap.set([wrap, overlay, close], { opacity: 0 });
	gsap.set(modal, { scaleX: 0, transformOrigin: 'right center' });

	// Entrance animation.
	const mobileNavAnimation = gsap.timeline({
		defaults: {
			duration: 0.4,
			ease:     'sine.out',
		},
		paused:  true,
		onStart: () => {
			const scrollY = document.documentElement.style.getPropertyValue('--scroll-y');
			const { body } = document;
			body.style.position = 'fixed';
			body.style.top = `-${scrollY}`;
			body.style.paddingRight = 'var(--scrollbar-width, 15px)';
			if (stickyHeader) {
				stickyHeader.style.width = 'calc(100% - var(--scrollbar-width, 15px))';
			}
			if (header && wpPostData.isFrontPage) {
				header.style.width = 'calc(100% - var(--scrollbar-width, 15px))';
			}
			mobileNav.classList.add('v-mobile-nav--open');
			trap.activate();
		},
		onReverseComplete: () => {
			const { body } = document;
			const scrollY = body.style.top;
			body.style.position = '';
			body.style.top = '';
			body.style.paddingRight = '';
			if (stickyHeader) {
				stickyHeader.style.width = '';
			}
			if (header && wpPostData.isFrontPage) {
				header.style.width = '';
			}
			window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
			mobileNav.classList.remove('v-mobile-nav--open');
			if (trap) {
				trap.deactivate();
			}
		},
	})
		.to(overlay, { opacity: 1 })
		.to(modal, { scaleX: 1 }, '<+75%')
		.to(wrap, { opacity: 1 }, '>')
		.to(close, { opacity: 1 }, '<+50%');

	/**
	 * Toggle mobile nav.
	 *
	 * @param {object} e - Event.
	 */
	const toggleNav = (e) => {
		if (e.detail.open) {
			if (e.detail.instant) {
				mobileNavAnimation.progress(1, false);
			} else {
				mobileNavAnimation.play();
			}
		} else if (e.detail.instant) {
			mobileNavAnimation.progress(0, false);
		} else {
			mobileNavAnimation.reverse();
		}
	};
	document.addEventListener('v-mobile-nav-toggle', toggleNav);

	// Close nav.
	close.addEventListener('click', () => {
		const event = new CustomEvent('v-mobile-nav-toggle', { detail: { open: false } });
		document.dispatchEvent(event);
	});

	// Track scroll position.
	window.addEventListener('scroll', () => {
		document.documentElement.style.setProperty('--scroll-y', `${window.scrollY}px`);
	});

	const mediaQuery = window.matchMedia('(max-width: 1050px)');
	mediaQuery.addEventListener('change', () => {
		if (!mediaQuery.matches) {
			const event = new CustomEvent('v-mobile-nav-toggle', { detail: { open: false, instant: true } });
			document.dispatchEvent(event);
		}
	});

	const mainNav = mobileNav.querySelector('.v-mobile-nav__main-nav');
	const mainNavScroller = mainNav.querySelector('.v-mobile-nav__main-wrap');

	let visibleHeight = 0;
	let scrollableHeight = 0;

	/**
	 * Set heights for scroll.
	 */
	const setHeights = () => {
		visibleHeight = mainNavScroller.offsetHeight;
		scrollableHeight = mainNavScroller.scrollHeight;
	};
	setHeights();
	window.addEventListener('resize', debounce(setHeights, 200, true));

	/**
	 * Set classes for scroll indicators.
	 */
	const setScrollIndicators = () => {
		const scrollPos = mainNavScroller.scrollTop;
		if (scrollPos > 10) {
			mainNav.classList.add('v-mobile-nav__main-nav--scrollable-up');
		} else {
			mainNav.classList.remove('v-mobile-nav__main-nav--scrollable-up');
		}

		if (scrollPos + visibleHeight < scrollableHeight - 10) {
			mainNav.classList.add('v-mobile-nav__main-nav--scrollable-down');
		} else {
			mainNav.classList.remove('v-mobile-nav__main-nav--scrollable-down');
		}
	};
	setScrollIndicators();
	mainNavScroller.addEventListener('scroll', setScrollIndicators);
	window.addEventListener('resize', debounce(setScrollIndicators, 200, true));

	const submenus = mobileNav.querySelectorAll('.v-mobile-nav__sub-list-wrap');
	[...submenus].forEach((submenu) => {
		const trigger = submenu.parentNode.querySelector('.v-mobile-nav__main-link');
		const subitems = submenu.querySelectorAll('.v-mobile-nav__sub-item');
		let isAnimating = false;
		let isOpen = false;

		trigger.addEventListener('click', (e) => {
			e.preventDefault();

			if (isAnimating) {
				return;
			}

			isAnimating = true;

			if (!isOpen) {
				trigger.classList.add('v-mobile-nav__main-link--open');

				gsap.set(subitems, { opacity: 0 });
				gsap.set(submenu, { display: 'block', opacity: 0 });
				const targetHeight = submenu.offsetHeight;
				gsap.set(submenu, { height: 0 });

				gsap.timeline({
					defaults: {
						ease: 'sine.out',
					},
					onComplete: () => {
						gsap.set([submenu, ...subitems], { clearProps: 'all' });
						submenu.classList.add('v-mobile-nav__sub-list-wrap--open');
						isAnimating = false;
						isOpen = true;
					},
				})
					.to(submenu, {
						opacity:  1,
						duration: 0.1,
					})
					.to(submenu, {
						height:   targetHeight,
						duration: 0.4,
					}, '<')
					.to(subitems, {
						opacity:  1,
						stagger:  0.05,
						duration: 0.2,
					}, '<+.1');
			} else {
				trigger.classList.remove('v-mobile-nav__main-link--open');

				gsap.timeline({
					defaults: {
						ease: 'sine.out',
					},
					onComplete: () => {
						gsap.set([submenu, ...subitems], { clearProps: 'all' });
						submenu.classList.remove('v-mobile-nav__sub-list-wrap--open');
						isAnimating = false;
						isOpen = false;
					},
				})
					.to(subitems, {
						opacity:  0,
						stagger:  -0.05,
						duration: 0.2,
					})
					.to(submenu, {
						height:   0,
						duration: 0.4,
					}, '>-.3')
					.to(submenu, {
						opacity:  0,
						duration: 0.1,
					}, '>-.1');
			}
		});
	});
};

export default vMobileNav;
